import { useSyncExternalStore, useCallback, useEffect } from "react";
import { toast } from "sonner";

interface CopiedLineItem {
  tupleId: string;
  lineItemId: string;
  pageId: string;
  lineItem: string;
}

interface CopyPasteState {
  items: CopiedLineItem[];
  estimateId: string | null;
}

/**
 * Module-level store for clipboard state, shared across SelectionToolbar, Line, and Room.
 * Lives outside React so clipboard persists across route changes (unlike Redux selectedLineItems
 * which gets cleared on navigation). Uses useSyncExternalStore to subscribe React components.
 * See: https://react.dev/reference/react/useSyncExternalStore
 */
const clipboardStore = {
  state: { items: [], estimateId: null } as CopyPasteState,
  listeners: new Set<() => void>(),

  setState(newState: Partial<CopyPasteState>) {
    this.state = { ...this.state, ...newState };
    this.listeners.forEach((l) => l());
  },

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  },

  getSnapshot() {
    return this.state;
  },
};

export function clearClipboard() {
  clipboardStore.setState({ items: [], estimateId: null });
}

export function useCopyPaste(estimateId?: string) {
  const { items: copiedLineItems, estimateId: clipboardEstimateId } = useSyncExternalStore(
    (l) => clipboardStore.subscribe(l),
    () => clipboardStore.getSnapshot(),
  );

  useEffect(() => {
    if (estimateId && clipboardEstimateId && estimateId !== clipboardEstimateId) {
      clearClipboard();
    }
  }, [estimateId, clipboardEstimateId]);

  const copyItems = useCallback(
    (items: CopiedLineItem[]) => {
      clipboardStore.setState({ items: [...items], estimateId: estimateId ?? null });
      toast.success(`${items.length} item${items.length > 1 ? "s" : ""} copied`, {
        className: "!text-green-500 !bg-green-500/10 !border-green-500",
      });
    },
    [estimateId],
  );

  return { copiedLineItems, copyItems, clearCopiedItems: clearClipboard };
}
